import {
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import allianzLogo from '../../../../../../assets/allianz.svg';
import { ALLIANZ_BLUE } from '../constants';
import { DualCTABannerDetails } from '../GovernmentDataPartnerBannerWithDataV2';
import { WebLink } from '../../../../../../app-kit/components/WebLink/WebLink';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';
import { deepOmitTypeName } from '../../../../../../analytics/helpers/deepOmitTypeName';

const StyledCard = styled.div`
  position: relative;
  display: flex;
  min-height: 188px;
  border-radius: ${radiusHelper('medium')};
  overflow: hidden;

  ${mediaHelper('tabletMin')} {
    height: 186px;
    min-height: unset;
  }
`;

const OverlayLogo = styled.img`
  display: none;

  ${mediaHelper('tabletMin')} {
    position: absolute;
    top: 126px;
    right: 0;
    display: block;
    background-color: ${paletteHelper('backgroundPrimary')};
    width: 108px;
    height: 36px;
    z-index: 10;
    padding: ${spacingHelper('extraSmall')} ${spacingHelper('small')};
  }
`;

const ContentLogo = styled.img`
  display: block;
  width: 97px;
  height: 24px;
  filter: brightness(0) invert(1);
  z-index: 10;

  ${mediaHelper('tabletMin')} {
    display: none;
  }
`;

const BannerImage = styled.img`
  display: none;
  width: 50%;
  object-fit: cover;
  object-position: top;

  ${mediaHelper('tabletMin')} {
    display: block;
  }
`;

const BannerContentContainer = styled.div`
  background-color: ${ALLIANZ_BLUE};
  width: 100%;
  padding: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacingHelper('small')};

  ${mediaHelper('tabletMin')} {
    width: 50%;
    min-width: 320px;
    padding: ${spacingHelper('large')};
  }
`;

const BannerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('twoExtraSmall')};
`;

const BannerTextAndLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('small')};
`;

const BannerTitle = styled.div`
  color: ${paletteHelper('backgroundPrimary')};
  ${typographyHelper('subtitle02')};

  ${mediaHelper('tabletMin')} {
    min-width: 255px;
  }
`;

const BannerDescription = styled.div`
  color: ${paletteHelper('backgroundPrimary')};
  ${typographyHelper('body02')};

  ${mediaHelper('tabletMin')} {
    min-width: 255px;
  }
`;

const BannerCTAContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('extraSmall')};
`;

const CTAHeadline = styled.div`
  color: ${paletteHelper('backgroundPrimary')};
  ${typographyHelper('body02')};
`;

const DualCTAContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacingHelper('extraSmall')};
`;

const StyledWebLink = styled(WebLink)`
  color: ${ALLIANZ_BLUE};
  background-color: ${paletteHelper('backgroundPrimary')};
  flex-grow: 0;

  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background-color: ${paletteHelper('backgroundPrimary')};
    color: ${ALLIANZ_BLUE};
  }
`;

const Disclaimer = styled.div`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
`;

const trackBannerClick = (
  linkTracking: DualCTABannerDetails['link1Tracking'] | DualCTABannerDetails['link2Tracking'],
): void => {
  if (!linkTracking) return;
  const event: DataGetter = () => deepOmitTypeName(linkTracking);
  trackEvent(event);
};

interface DualCTABannerProps {
  bannerDetails: DualCTABannerDetails;
  imageSource: string;
}

export const DualCTABanner = ({ bannerDetails, imageSource }: DualCTABannerProps) => {
  const {
    title,
    description,
    ctaHeadline,
    link1,
    link2,
    disclaimer,
    link1Tracking,
    link2Tracking,
  } = bannerDetails;

  return (
    <>
      <StyledCard data-testid="government-data-partner-banner-v2">
        <OverlayLogo src={allianzLogo} alt="Allianz Logo" />
        <BannerContentContainer>
          <BannerTextAndLogoContainer>
            <ContentLogo src={allianzLogo} alt="Allianz Logo" />
            <BannerTextContainer>
              <BannerTitle>{title}</BannerTitle>
              <BannerDescription>{description}</BannerDescription>
            </BannerTextContainer>
          </BannerTextAndLogoContainer>
          <BannerCTAContainer>
            <CTAHeadline>{ctaHeadline}</CTAHeadline>
            <DualCTAContainer>
              <StyledWebLink
                url={link1.url}
                isExternal={link1.isExternal}
                title={link1.title}
                variant="button-primary"
                tracking={link1.tracking}
                onClick={() => trackBannerClick(link1Tracking)}
              />
              <StyledWebLink
                url={link2.url}
                isExternal={link2.isExternal}
                title={link2.title}
                variant="button-primary"
                tracking={link2.tracking}
                onClick={() => trackBannerClick(link2Tracking)}
              />
            </DualCTAContainer>
          </BannerCTAContainer>
        </BannerContentContainer>
        <BannerImage src={imageSource} alt="Allianz Renewal Banner" />
      </StyledCard>
      <Disclaimer>{disclaimer}</Disclaimer>
    </>
  );
};
